import { getAmplitudeInstance } from '@alltrails/shared/utils/amplitudeHelpers';
import {
  DEFAULT_CONTENT_PRIVACY_CHANGED,
  FOLLOWER_REQUESTS_SETTING_CHANGED,
  INDIVIDUAL_CONTENT_PRIVACY_CHANGED,
  USER_RELATIONSHIPS_PRIVACY_CHANGED
} from './constants/AmplitudeEvent';
import { VISIBILITY_FOLLOWERS_ONLY, VISIBILITY_PRIVATE, VISIBILITY_PUBLIC } from './privacy_policy_helpers';

type Visibility = typeof VISIBILITY_PUBLIC | typeof VISIBILITY_FOLLOWERS_ONLY | typeof VISIBILITY_PRIVATE;

type ContentVisibilitySetting = {
  visibility: Visibility;
};

export type PrivacyPolicy = {
  content: {
    activities: ContentVisibilitySetting;
    completedTrails: ContentVisibilitySetting;
    customMaps: ContentVisibilitySetting;
    lists: ContentVisibilitySetting;
  };
  profile: {
    socialNetwork: ContentVisibilitySetting;
  };
  socialNetwork: {
    followRequestsRequired: boolean;
  };
  version: string;
};

type ContentCategory = 'activities' | 'completedTrails' | 'customMaps' | 'lists';

const categories: ContentCategory[] = ['activities', 'completedTrails', 'customMaps', 'lists'];

const getFollowerRequestsSettingAmplitudeValue = (status: boolean) => (status ? 'on' : 'off');

const getVisibilitySettingAmplitudeValue = (status: Visibility) => {
  switch (status) {
    case VISIBILITY_PUBLIC:
      return 'public visibility';
    case VISIBILITY_FOLLOWERS_ONLY:
      return 'followers only visibility';
    case VISIBILITY_PRIVATE:
    default:
      return 'private visibility';
  }
};

const getContentTypeAmplitudeValue = (content: ContentCategory) => {
  switch (content) {
    case 'activities':
      return 'activities';
    case 'completedTrails':
      return 'completed trails';
    case 'customMaps':
      return 'custom maps';
    case 'lists':
    default:
      return 'lists';
  }
};

export const logPrivacyPolicyAmplitudeEvents = (oldPolicy: PrivacyPolicy, newPolicy: PrivacyPolicy) => {
  if (oldPolicy && newPolicy && oldPolicy !== newPolicy) {
    if (oldPolicy.socialNetwork.followRequestsRequired !== newPolicy.socialNetwork.followRequestsRequired) {
      getAmplitudeInstance().logEvent(FOLLOWER_REQUESTS_SETTING_CHANGED, {
        'old status': getFollowerRequestsSettingAmplitudeValue(oldPolicy.socialNetwork.followRequestsRequired),
        'new status': getFollowerRequestsSettingAmplitudeValue(newPolicy.socialNetwork.followRequestsRequired)
      });
    }

    if (oldPolicy.profile.socialNetwork !== newPolicy.profile.socialNetwork) {
      getAmplitudeInstance().logEvent(USER_RELATIONSHIPS_PRIVACY_CHANGED, {
        'old status': getVisibilitySettingAmplitudeValue(oldPolicy.profile.socialNetwork.visibility),
        'new status': getVisibilitySettingAmplitudeValue(newPolicy.profile.socialNetwork.visibility)
      });
    }

    categories.forEach(category => {
      if (oldPolicy.content[category] !== newPolicy.content[category]) {
        getAmplitudeInstance().logEvent(DEFAULT_CONTENT_PRIVACY_CHANGED, {
          'content type': getContentTypeAmplitudeValue(category),
          'old status': getVisibilitySettingAmplitudeValue(oldPolicy.content[category].visibility),
          'new status': getVisibilitySettingAmplitudeValue(newPolicy.content[category].visibility)
        });
      }
    });
  }
};

export const logIndividualContentPrivacyChangedAmplitudeEvent = (
  contentType: ContentCategory,
  contentId: number,
  defaultVisibility: Visibility,
  oldVisibility: Visibility,
  newVisibility: Visibility
) => {
  if (oldVisibility !== newVisibility) {
    getAmplitudeInstance().logEvent(INDIVIDUAL_CONTENT_PRIVACY_CHANGED, {
      'content id': contentId,
      'content type': getContentTypeAmplitudeValue(contentType),
      'content default setting': getVisibilitySettingAmplitudeValue(defaultVisibility),
      'old status': getVisibilitySettingAmplitudeValue(oldVisibility),
      'new status': getVisibilitySettingAmplitudeValue(newVisibility)
    });
  }
};
